import { BCMSCurrency, BCMSDate, BCMSRecord, DisabledPrompt, useWorkflowStep } from "@bcm-dev/bcms-vite";
import { getDefaultAutopay, getWorkflowSteps } from "compid/workflows/AddAutoPay";
import { Autopay, Biller } from "model";

import ButtonContainer from "compid/shared/containers/ButtonContainer";
import PageLayoutContainer from "compid/shared/containers/PageLayoutContainer";
import CenterColumnContainer from "compid/shared/containers/PageLayoutContainer/containers/CenterColumnContainer";
import PageView from "compid/shared/containers/PageViewContainer";
import BillerView from "compid/shared/views/BillerView";
import TableView, { TableRowView } from "compid/shared/views/TableView";
import UIButton from "compid/ui/UIButton";
import { SVGAsset } from "res";
import "./styles.css";

export default function ConfirmAutoPayDetails(props) {
  // Get the workflowStep and initalize model classes
  const workflowStep = useWorkflowStep(getDefaultAutopay, getWorkflowSteps);
  BCMSRecord.globalControllerFor([Biller, Autopay], workflowStep.globalController);

  const biller = workflowStep.read("biller");
  const autopay = workflowStep.read("autopay");
  const editing = workflowStep.read("editing");
  const isVirtualCard = biller.paymentType === "virtualCard";

  // Get the email from the model
  const { emails } = workflowStep.model;
  const email = emails[0].emailName ?? "e@example.com";
  const pageTitle = editing ? "Your changes are saved" : "Your autopay is confirmed";

  const isCurrency = autopay.alwaysPay instanceof BCMSCurrency;
  const date = autopay.deliverBy instanceof BCMSDate ? autopay.formatDate() : autopay.deliverBy;

  const showFinalDelivery = autopay.duration === "Until a specific date";
  const finalDeliveryDate = autopay.finalDeliveryBy instanceof BCMSDate ? autopay.formatDate("finalDeliveryBy") : autopay.finalDeliveryBy;
  const showAmountOfLast = autopay.duration === "For a specific number of payments" || autopay.duration === "Until a specific date";
  //const amountOfLast = parseFloat(autopay.lastPaymentAmount.amount) === 0 ? "Not set" : autopay.lastPaymentAmount.format();
  const amountOfLast = parseFloat(autopay.lastPaymentAmount.amount) === 0 || isNaN(parseFloat(autopay.lastPaymentAmount.amount)) ?
  "Not set" :
  autopay.lastPaymentAmount.format();
  
  let alwaysPay;

  if (autopay.alwaysPay === "Fixed amount") { alwaysPay = autopay.fixed.format(); }
  else { alwaysPay = isCurrency ? autopay.alwaysPay.format() : autopay.alwaysPay; }

  const isScheduled = autopay.notificationScheduled && "is due";
  const isSent = autopay.notificationSent && "has been sent";
  const isLast = (autopay.notificationLastOne && isCurrency) && "is the last one in the series";

  const removeNotifications = isScheduled === false && isSent === false && isLast === false;

  let notifications = [isScheduled, isSent, isLast].reduce((result, item) => item !== false ? `${ result } ${ item },` : result, "");
  notifications = notifications.slice(0, notifications.length - 1);

  return (
    <PageView className="confirm-autopay-details-page">
      <div className="page-sub-header flex-container flex-container align-center space-between">
        <p className="semi-bold-text">AUTOPAY CONFIRMATION</p>
        <DisabledPrompt>
          <div className="print-icon-container flex-container flex-horizontal align-center link clickable">
            <SVGAsset id="PrinterIcon" className="print-icon right-icon icon" />
            <p class="link print-text">Print</p>
          </div>
        </DisabledPrompt>
        
      </div>
      
      <PageLayoutContainer>
        <div className="flex-container flex-vertical flex-width">
          <div className="flex-height flex-container flex-vertical upper-content-container">
            <BillerView biller={biller} />
          </div>

          <div className="flex-height flex-width flex-container all-center">
            <CenterColumnContainer>
              <div className="flex-container flex-vertical all-center form-row icon-container">
                <SVGAsset id="CheckmarkIcon" className="green-icon" />
                <p className="semi-bold-text">{ pageTitle }</p>
              </div>
              <div className="form-row">
                <TableView>
                  {isCurrency && <TableRowView rowClassName="deliver-by-row delivery-date-label" label="First Delivery Date" labelClass="semi-bold-text" data={<><p>{date}</p><p className="fine-print" dataClass="fine-print"><i>Payments that fall on a weekend or holiday will be changed to previous business day.</i></p></>} dataClass="delivery-date" />}
                  {!isCurrency && <TableRowView rowClassName="deliver-by-row" label="Deliver by" labelClass="semi-bold-text" data={<><p>{date}</p><p className="fine-print" dataClass="fine-print"><i>Payments that fall on a weekend or holiday will be changed to previous business day.</i></p></>} dataClass="delivery-date" />}

                  <TableRowView label="Pay from" labelClass="semi-bold-text" data={`${autopay.payFrom.name} ${autopay.payFrom.number}`} />
                  <TableRowView label="Always pay" labelClass="semi-bold-text" data={alwaysPay} />

                  {
                    isCurrency &&
                    <>
                      <TableRowView label="Frequency" labelClass="semi-bold-text" data={autopay.frequency} />
                      <TableRowView label="Duration" labelClass="semi-bold-text" data={autopay.duration} />
                    </>
                  }

                  {showFinalDelivery && <TableRowView rowClassName="deliver-by-row" label="Final delivery date" labelClass="semi-bold-text" data={<><p>{finalDeliveryDate}</p><p className="fine-print" dataClass="fine-print"><i>Payments that fall on a weekend or holiday will be changed to previous business day.</i></p></>} dataClass="delivery-date" />}
                  {showAmountOfLast && <TableRowView label="Amount of last payment" labelClass="semi-bold-text" data={amountOfLast} />}

                  {/* Maximum payment only shows if Maximum payment text input was filled in during the last workflow step (Add Autopay Page) */}
                  {(alwaysPay === "Amount due" && parseFloat(autopay.maximum.amount) > 0) && <TableRowView label="Maximum payment" labelClass="semi-bold-text" data={autopay.maximum.format()} />}

                  {biller.paymentType === "paper" && <TableRowView label="Memo" labelClass="semi-bold-text" data={autopay.memo} />}
                </TableView>
              </div>

              { isVirtualCard && <span className="sub-text">{ Biller.getVirtualCardDisclaimer() }</span> }

              {
                !removeNotifications &&
                <div className="text-container">
                  <p>We'll send an email to <b data-point="Autopay_Confirm_Email">{email}</b> when the payment { notifications }.</p>
                </div>
              }
            

              <ButtonContainer className="form-button-container">
                <UIButton className="secondary-button" onClick={() => workflowStep.navigate(`/billerDetails/details/${biller.id}`)} dataPoint="Autopay_Confirm_Biller_Details">Go to biller details</UIButton>
                <UIButton className="primary-button" onClick={() => workflowStep.navigate("/payBills")} dataPoint="Autopay_Confirm_Pay_Bills">Go to pay bills</UIButton>
              </ButtonContainer>
            </CenterColumnContainer>
          </div>
        </div>
      </PageLayoutContainer>
    </PageView>
  );
}